import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MonitorSmartphone } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/lib/seo";

const IMG_GLASS =
    "https://images.unsplash.com/photo-1725913496276-47d7147da99f?auto=format&fit=crop&w=1600&q=80";

const TIMELINE = [
    { t: "Contatto", d: "Il primo passo è un contatto: racconti la tua esigenza tramite il form o i recapiti del sito." },
    { t: "Analisi esigenza", d: "Analizziamo la situazione e definiamo insieme come possiamo supportarti al meglio." },
    { t: "Raccolta documenti", d: "La documentazione viene condivisa attraverso canali digitali semplici e sicuri, concordati con te." },
    { t: "Gestione", d: "Seguiamo pratiche e attività in modo organizzato e continuativo." },
    { t: "Aggiornamenti", d: "Restiamo in contatto e ti aggiorniamo sull'andamento del lavoro." },
];

const FAQ = [
    {
        q: "Devo essere a Isernia per lavorare con voi?",
        a: "No. Molte attività possono essere gestite completamente da remoto.",
    },
    {
        q: "Come invio la documentazione?",
        a: "La documentazione viene condivisa attraverso strumenti digitali semplici e sicuri, scelti e concordati insieme in base alle esigenze della tua attività: l'obiettivo è un flusso ordinato e accessibile, senza complicazioni.",
    },
    {
        q: "Posso contattarvi anche se la mia azienda si trova fuori regione?",
        a: "Sì, la società può lavorare anche con clienti a distanza.",
    },
];

export default function FullRemote() {
    useSEO(
        "Full Remote | Service & Business SRLS",
        "I servizi di Service & Business SRLS anche completamente da remoto: contatto, analisi, raccolta documenti, gestione e aggiornamenti, ovunque si trovi la tua impresa."
    );

    const [open, setOpen] = useState(0);

    return (
        <>
            <PageHero
                eyebrow="Full Remote"
                lines={["I nostri servizi, anche", "completamente da remoto."]}
                sub="Puoi affidarti a Service & Business anche se non ti trovi a Isernia: un rapporto professionale semplice, organizzato e accessibile ovunque ti trovi."
                gradientLast={false}
            />

            {/* TIMELINE */}
            <section className="py-16 sm:py-20">
                <div className="wrap grid lg:grid-cols-[0.9fr_1.1fr] gap-14">
                    <Reveal>
                        <p className="eyebrow mb-6">Il processo</p>
                        <h2 className="h2">Dal primo contatto agli aggiornamenti.</h2>
                        <p className="mt-6 lead">
                            La gestione digitale delle attività ci permette di seguire pratiche e servizi da remoto, con
                            un metodo chiaro in cinque passaggi.
                        </p>
                        <div className="relative overflow-hidden rounded-3xl mt-10 hidden lg:block">
                            <img
                                src={IMG_GLASS}
                                alt="Facciate vetrate di un edificio moderno che riflettono il cielo"
                                className="w-full h-64 object-cover"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-ink/40" />
                            <div className="absolute bottom-6 left-6 flex items-center gap-3">
                                <MonitorSmartphone className="h-5 w-5 text-sky-400" />
                                <p className="font-mono text-[10px] tracking-[0.28em] text-white uppercase">
                                    Operatività digitale, senza confini
                                </p>
                            </div>
                        </div>
                    </Reveal>
                    <div className="relative">
                        <div className="absolute left-[22px] top-2 bottom-2 w-px" style={{ background: "linear-gradient(180deg, rgba(65,198,241,0.55), rgba(10,95,194,0.08))" }} />
                        <div className="space-y-10">
                            {TIMELINE.map((s, i) => (
                                <Reveal key={s.t} delay={i * 0.06}>
                                    <div className="relative flex gap-6" data-testid={`remote-timeline-step-${i + 1}`}>
                                        <span
                                            className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-sky-400/40 bg-ink font-mono text-xs text-sky-400"
                                        >
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <div className="pt-1.5">
                                            <h3 className="font-display text-xl font-semibold text-white">{s.t}</h3>
                                            <p className="text-sm text-slate-400 leading-relaxed mt-2 max-w-lg">{s.d}</p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 sm:py-24">
                <div className="wrap max-w-3xl">
                    <Reveal>
                        <p className="eyebrow mb-6">FAQ</p>
                        <h2 className="h2">Domande frequenti.</h2>
                    </Reveal>
                    <div className="mt-10 space-y-4">
                        {FAQ.map((f, i) => {
                            const isOpen = open === i;
                            return (
                                <Reveal key={f.q} delay={i * 0.05}>
                                    <div className="card-dark rounded-2xl overflow-hidden" data-testid={`faq-item-${i + 1}`}>
                                        <button
                                            type="button"
                                            data-testid={`faq-toggle-${i + 1}`}
                                            aria-expanded={isOpen}
                                            onClick={() => setOpen(isOpen ? -1 : i)}
                                            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                                        >
                                            <span className="font-display text-base sm:text-lg font-semibold text-white">{f.q}</span>
                                            <ChevronDown className={"h-5 w-5 shrink-0 text-sky-400 transition-transform duration-300 " + (isOpen ? "rotate-180" : "")} />
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                                    className="overflow-hidden"
                                                >
                                                    <p className="px-6 pb-6 text-sm text-slate-400 leading-relaxed">{f.a}</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <CTASection
                title="Parliamo della tua attività."
                text="Raccontaci la tua esigenza: possiamo valutare insieme come supportarti, anche a distanza."
                testid="remote-final-cta"
            />
        </>
    );
}
